import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import type { MonitoringSession, StreamSummary } from '@shared/protocol';
import { api } from '../lib/api';
import { useDocumentTitle } from '../lib/branding';
import { Notice } from '../components/Status';

const when = (t: number) => new Date(t).toLocaleString('en-GB', { hour12: false });
function dur(ms: number) {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min ${s % 60} s`;
}

/** Monitoring sessions for a stream, each with CSV and PDF compliance reports. */
export function ReportsPage() {
  const id = Number(useParams().id);
  const [stream, setStream] = useState<StreamSummary | null>(null);
  const [sessions, setSessions] = useState<MonitoringSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle(stream ? `Reports: ${stream.name}` : 'Reports');

  useEffect(() => {
    Promise.all([api<{ stream: StreamSummary }>('GET', `/api/streams/${id}`), api<{ sessions: MonitoringSession[] }>('GET', `/api/streams/${id}/sessions`)])
      .then(([s, r]) => {
        setStream(s.stream);
        setSessions(r.sessions);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the sessions.'));
  }, [id]);

  return (
    <div className="page narrow">
      <div className="page-header">
        <div>
          <Link to={`/streams/${id}`} className="back-link">
            <ArrowLeft size={14} aria-hidden /> {stream?.name ?? 'Stream'}
          </Link>
          <h1>Compliance reports</h1>
          <p className="muted">A session runs from starting to stopping monitoring. Reports include the compliance status per preset and profile, statistics, graphs, findings and alerts.</p>
        </div>
      </div>
      {error && <Notice level="fail">{error}</Notice>}
      <div className="card">
        {sessions === null ? (
          <div className="empty">Loading</div>
        ) : sessions.length === 0 ? (
          <div className="empty">No monitoring sessions yet. Start monitoring the stream to record one.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Duration</th>
                <th className="actions">Export</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>
                    {when(s.startedAt)}
                    {s.endedAt === null && <span className="badge badge-pass" style={{ marginLeft: 8 }}>In progress</span>}
                  </td>
                  <td className="num">{dur((s.endedAt ?? Date.now()) - s.startedAt)}</td>
                  <td className="actions">
                    <a className="btn btn-sm" href={`/api/sessions/${s.id}/report.pdf`}>
                      <Download size={14} aria-hidden /> PDF
                    </a>{' '}
                    <a className="btn btn-sm" href={`/api/sessions/${s.id}/report.csv`}>
                      <Download size={14} aria-hidden /> CSV
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
